import { useEffect, useRef } from 'react';
import type { IconCategory, MapObject, OperationPlanDTO } from '@warplan/shared';
import {
  usePatchObjectSource as usePatchMapObject,
  useUpdateOperationPlanSource as useUpdateOperationPlan,
} from './map-data-source.js';

/**
 * Bidirectional sync between the 9 operation-objective map icons (with notes)
 * and the operation plan's `priorities[]` list.
 *
 * Mapping (9 slots, capped at 9):
 *   - slot 0..5 → `OPERATION_ATTACK_OBJECTIVE_{1,2,3,A,B,C}` (kind `op_attack`)
 *   - slot 6..8 → `OPERATION_DEFEND_OBJECTIVE_{1,2,3}`       (kind `op_defend`)
 *
 * Rules:
 *   - When an icon's note changes and diverges from the matching plan slot,
 *     the note is pushed to the plan.
 *   - When a plan slot changes and diverges from icons of the matching
 *     iconId, the new text is patched into every such icon's `metadata.note`.
 *   - Direction is resolved by comparing each side's current value to the
 *     last value the sync hook observed; only the side that actually changed
 *     is pushed. If both changed at once, the map side wins (most recent
 *     explicit user action).
 *   - If multiple icons share the same priority slot, the latest one (by
 *     `updatedAt`) wins as the source of truth.
 */
export function usePrioritySync(
  objects: ReadonlyArray<MapObject>,
  plan: OperationPlanDTO | null,
): void {
  const patchMut = usePatchMapObject();
  const updatePlanMut = useUpdateOperationPlan();

  // Per-slot last observed (mapNote, planText) — used to detect which side changed.
  const lastSeenRef = useRef<Map<number, { mapNote: string; planText: string }>>(
    new Map(),
  );

  useEffect(() => {
    if (!plan) return;

    // Aggregate the canonical map note per slot (latest icon by updatedAt).
    const latestNoteBySlot = new Map<number, { note: string; updatedAt: string }>();
    for (const obj of objects) {
      const slot = priorityIconIdToSlot(obj.metadata.iconId);
      if (slot < 0) continue;
      const note = (obj.metadata.note ?? '').trim();
      const prev = latestNoteBySlot.get(slot);
      if (!prev || prev.updatedAt < obj.updatedAt) {
        latestNoteBySlot.set(slot, { note, updatedAt: obj.updatedAt });
      }
    }

    const slotsToCheck = new Set<number>([
      ...latestNoteBySlot.keys(),
      ...plan.priorities.map((_, i) => i),
    ]);

    let planPatch: string[] | null = null;

    for (const slot of slotsToCheck) {
      const mapNote = latestNoteBySlot.get(slot)?.note ?? '';
      const planText = (plan.priorities[slot] ?? '').trim();
      if (mapNote === planText) {
        lastSeenRef.current.set(slot, { mapNote, planText });
        continue;
      }

      const last = lastSeenRef.current.get(slot);
      const mapChanged = !last || last.mapNote !== mapNote;
      const planChanged = !last || last.planText !== planText;

      const hydrateMapFromPlan = !mapNote && !!planText;
      const pushMapToPlan = !hydrateMapFromPlan && (
        (mapChanged && !planChanged) ||
        (mapChanged && planChanged) ||
        (!mapChanged && !planChanged)
      );

      if (pushMapToPlan) {
        if (!mapNote) {
          lastSeenRef.current.set(slot, { mapNote, planText });
          continue;
        }
        if (!planPatch) planPatch = [...plan.priorities];
        while (planPatch.length <= slot) planPatch.push('');
        planPatch[slot] = mapNote;
        lastSeenRef.current.set(slot, { mapNote, planText: mapNote });
      } else {
        const iconId = slotToPriorityIconId(slot);
        if (!planText) {
          lastSeenRef.current.set(slot, { mapNote, planText });
          continue;
        }
        for (const obj of objects) {
          if (obj.metadata.iconId !== iconId) continue;
          const objNote = (obj.metadata.note ?? '').trim();
          if (objNote === planText) continue;
          patchMut.mutate({
            id: obj.id,
            patch: {
              metadata: { iconId, note: planText },
            },
          });
        }
        lastSeenRef.current.set(slot, { mapNote: planText, planText });
      }
    }

    if (planPatch) {
      updatePlanMut.mutate({ priorities: planPatch });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [objects, plan]);
}

// ---------------------------------------------------------------------------
// Slot helpers (exported so UI components can render real PNG badges)
// ---------------------------------------------------------------------------

export const PRIORITY_SLOT_COUNT = 9;

/**
 * Ordered slot → (iconId, kind). Slots 0..5 are ATTACK objectives 1/2/3/A/B/C,
 * slots 6..8 are DEFEND objectives 1/2/3.
 */
export const PRIORITY_SLOTS: ReadonlyArray<{
  iconId: string;
  kind: IconCategory;
}> = [
  { iconId: 'OPERATION_ATTACK_OBJECTIVE_1', kind: 'op_attack' },
  { iconId: 'OPERATION_ATTACK_OBJECTIVE_2', kind: 'op_attack' },
  { iconId: 'OPERATION_ATTACK_OBJECTIVE_3', kind: 'op_attack' },
  { iconId: 'OPERATION_ATTACK_OBJECTIVE_A', kind: 'op_attack' },
  { iconId: 'OPERATION_ATTACK_OBJECTIVE_B', kind: 'op_attack' },
  { iconId: 'OPERATION_ATTACK_OBJECTIVE_C', kind: 'op_attack' },
  { iconId: 'OPERATION_DEFEND_OBJECTIVE_1', kind: 'op_defend' },
  { iconId: 'OPERATION_DEFEND_OBJECTIVE_2', kind: 'op_defend' },
  { iconId: 'OPERATION_DEFEND_OBJECTIVE_3', kind: 'op_defend' },
];

/** Slots whose kind is `op_attack` (indices 0–5). */
export const ATTACK_PRIORITY_SLOTS = PRIORITY_SLOTS.filter(
  (s) => s.kind === 'op_attack',
);
/** Slots whose kind is `op_defend` (indices 6–8). */
export const DEFEND_PRIORITY_SLOTS = PRIORITY_SLOTS.filter(
  (s) => s.kind === 'op_defend',
);
export const ATTACK_SLOT_COUNT = ATTACK_PRIORITY_SLOTS.length; // 6
export const DEFEND_SLOT_COUNT = DEFEND_PRIORITY_SLOTS.length; // 3
/** Offset of the first DEFEND slot in the full PRIORITY_SLOTS array. */
export const DEFEND_SLOT_OFFSET = ATTACK_SLOT_COUNT; // 6

const SLOT_BY_ICON_ID: ReadonlyMap<string, number> = new Map(
  PRIORITY_SLOTS.map((s, i) => [s.iconId, i]),
);

export function priorityIconIdToSlot(iconId: string): number {
  return SLOT_BY_ICON_ID.get(iconId) ?? -1;
}

export function slotToPriorityIconId(slot: number): string {
  return PRIORITY_SLOTS[slot]?.iconId ?? '';
}

export function slotToPriorityKind(slot: number): IconCategory | null {
  return PRIORITY_SLOTS[slot]?.kind ?? null;
}
